import classNames from 'classnames';
import useWorldTheme from 'hooks/shared/useWorldTheme';
import type { ComponentProps, FC } from 'react';
import type { ITailwindSizes } from 'theme/theme.types';
import type { color } from 'types/shared/worlds';
import { getTracingEvents } from 'utils/amplitude';
import type { EventConfig } from 'utils/amplitude/amplitude.types';
import { XIconOutline } from 'utils/icons';
import { Loader } from '../Loader';

const GraphicButton: FC<
  Omit<ComponentProps<'button'>, 'color'> & {
    color?: color | 'red' | 'yellow';
    size?: keyof ITailwindSizes;
    icon?: FC<ComponentProps<'svg'>>;
    iconPlacement?: 'left' | 'right';
    loading?: boolean;
    tracingEvent?: EventConfig;
    ariaDisabled?: boolean;
    stopClickPropagation?: boolean;
    label?: string;
    description?: string;
    buttonGraphicSrc?: string;
    closable?: boolean;
    onClose?: () => void;
  }
> = (props) => {
  const {
    disabled,
    className: _className,
    onClick,
    //--
    iconPlacement = 'left',
    loading,
    icon: Icon,
    color,
    size = 'md',
    tracingEvent,
    ariaDisabled,
    label,
    description,
    buttonGraphicSrc,
    closable = false,
    onClose,
    ...rest
  } = props;
  const worldColor = useWorldTheme();

  const tracingEvents = getTracingEvents(
    tracingEvent ? { onClick: tracingEvent } : {},
    { onClick },
  );

  const theme = {
    expenses: {
      icon: 'border-expenses-100 bg-expenses',
    },
    revenues: {
      icon: 'border-revenues-100 bg-revenues',
    },
    taxes: {
      icon: 'border-taxes-100 bg-taxes',
    },
    bank: {
      icon: 'border-bank-100 bg-bank',
    },
    primary: {
      icon: 'border-primary-100 bg-primary',
    },
  };

  return (
    <button
      type="button"
      disabled={disabled || loading}
      {...(disabled || ariaDisabled
        ? { 'aria-disabled': true, tabIndex: -1 }
        : null)}
      className={classNames(
        'disabled:cursor-not-allowed group hover:bg-gray-50 transition-colors relative rounded-lg inline-flex items-center justify-start focus:outline-none bg-white border border-taxes-100',
        'p-4 pr-32',
        _className,
      )}
      onClick={
        disabled || ariaDisabled || loading ? undefined : tracingEvents.onClick
      }
      {...rest}
    >
      {loading ? <Loader size={size} className="mr-2" /> : ''}

      {Icon && !loading && iconPlacement === 'left' ? (
        <div
          className={classNames(
            'w-11 h-11 border-[10px] rounded-full flex items-center justify-center mr-4',
            theme[(color || worldColor) as keyof typeof theme].icon,
          )}
        >
          <Icon aria-hidden className="w-4 h-4 text-white" />
        </div>
      ) : (
        ''
      )}

      <div className="text-left">
        <p className="font-semibold text-taxes -mb-1">{label}</p>

        <span className="text-primary-700 text-sm">{description}</span>
      </div>

      {closable && onClose && (
        <div
          tabIndex={0}
          role="button"
          aria-label="Close"
          className="absolute top-2 right-2 z-10 flex-shrink-0 p-0.5 rounded-md hover:bg-gray-100 transition-colors focus:outline-none"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <XIconOutline className="w-5 h-5 text-[#989898]" />
        </div>
      )}

      <img
        className="absolute bottom-0 right-0 h-[45px] group-hover:h-[55px] transition-all"
        src={buttonGraphicSrc}
      />
    </button>
  );
};

export default GraphicButton;
